import { WorkerDesiredStateSchema, subscribesToSchema } from "./workers.schemas.js";
import { buildProvenanceEnvelopeSchema, mountPathSchema, releaseStatementSchema, releaseStatementSignatureSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/register-version.schemas.ts
/**
* Zod schemas for the version registration endpoints (CP).
*
*   POST /v1/releases/versions               — register a fixture version
*   POST /v1/releases/apps/:appId/versions   — register an app version (bundle ingest)
*/
const ServiceDeclarationSchema = z.object({
	name: z.string().min(1),
	imageTag: z.string().min(1),
	port: z.number().int().positive(),
	healthPath: z.string().min(1)
}).strict();
const FixtureDependencySchema = z.object({
	fixture: z.string().min(1),
	version: z.string().min(1)
}).strict();
const RegisterFixtureVersionDtoSchema = z.object({
	fixtureName: z.string().min(1),
	version: z.string().min(1),
	imageTag: z.string().min(1),
	bundleUrl: z.string().nullable().optional(),
	cssUrl: z.string().nullable().optional(),
	services: z.array(ServiceDeclarationSchema).optional(),
	workers: z.array(WorkerDesiredStateSchema).optional(),
	minPlatformVersion: z.string().min(1).optional(),
	bundleHash: z.string().min(1).optional(),
	fixtureDependencies: z.array(FixtureDependencySchema).optional(),
	expandMigrations: z.array(z.string()).optional(),
	contractMigrations: z.array(z.string()).optional()
}).strict();
/**
* The zod form of {@link SignedReleaseStatementBody}. Strict at the top level, and each member
* parses with its `@zion/sdk-contracts` schema, which is strict at every level.
*
* Input classes and outputs:
*
* 1. a statement that `releaseStatementSchema` accepts and a signature that
*    `releaseStatementSignatureSchema` accepts: the same object;
* 2. an extra top-level key: an `unrecognized_keys` issue;
* 3. a missing member, or a member that its schema refuses: an issue under that member path. That
*    covers an unsorted image list, a statement over 47,104 canonical bytes, an unknown kernel
*    right, an `alg` other than `ES256` and a `sig` over 128 characters.
*
* It never sorts, decodes or verifies: a parse proves the shape and the bounds, never the
* signature. No `.transform()`. No side effects.
*/
const SignedReleaseStatementBodySchema = z.strictObject({
	statement: releaseStatementSchema,
	signature: releaseStatementSignatureSchema
});
const RegisterAppVersionDtoSchema = z.object({
	artifactRef: z.string().min(1),
	channel: z.string().min(1).optional(),
	/**
	* 2026-10-03-kernel-apps WS-2a (R-024). The signed release statement of the published version.
	* Optional: a publish without it is the publish of today. The publish door (WS-8a) refuses a
	* version whose manifest asks a kernel right and carries no statement
	* (`release_statement_required`), and refuses a statement beside a manifest that asks none
	* (`release_statement_mismatch`). Absent and present are the only two states; there is no
	* `null`.
	*/
	releaseStatement: SignedReleaseStatementBodySchema.optional(),
	/**
	* 2026-10-15-signed-builds WS-0 (D3, D6). The DSSE envelope of the zion-build run that built
	* this version: an in-toto Statement v1 with an SLSA provenance v1 predicate, signed with the
	* `build-provenance` key. The member schema is `buildProvenanceEnvelopeSchema` from
	* `@zion/sdk-contracts` itself, so the wire and the verifier share one grammar; this package
	* declares no second envelope grammar.
	*
	* Optional: absent is the publish of today. Both publish arms carry it: this body (the session
	* arm, `POST /v1/releases/apps/:appId/versions`) and the run arm body
	* (`POST /v1/releases/apps/:appId/versions/run`), which release-engine extends from the
	* session body. The publish door (WS-3) verifies a present envelope with
	* `verifyBuildProvenance` and refuses one that fails any check in every mode
	* (`build_provenance_invalid`, `build_provenance_mismatch`,
	* `build_provenance_source_unmatched`). `BUILD_PROVENANCE_MODE` decides when an absent one is
	* refused (`build_provenance_required`). Absent and present are the only two states; there is
	* no `null`.
	*/
	buildProvenance: buildProvenanceEnvelopeSchema.optional()
}).strict();
const AppFixtureResultSchema = z.object({
	fixtureId: z.string().min(1),
	fixtureVersionId: z.string().min(1),
	imageRef: z.string().min(1)
}).strict();
const PromotedChannelSchema = z.object({
	channelId: z.string().min(1),
	channelName: z.string().min(1)
}).strict();
/** One publish-time warning returned to the author (2026-09-04-app-pools, WS-3 Step 4j-c). The shape is
*  `FixtureRuleIssue` from `@zion/sdk-contracts` with `severity` fixed to `'warning'`: `checkDeprecatedSurfaces`
*  produces it and the CP persists the same array as `app_compositions.publish_warnings`. */
const PublishWarningSchema = z.object({
	severity: z.literal("warning"),
	path: z.string().min(1),
	rule: z.string().min(1),
	message: z.string().min(1)
}).strict();
const RegisterAppVersionResponseDtoSchema = z.object({
	appVersionId: z.string().min(1),
	appId: z.string().min(1),
	version: z.string().min(1),
	buildTag: z.string().min(1),
	bundleDigest: z.string().min(1),
	artifactRef: z.string().min(1),
	status: z.enum([
		"resolved",
		"promoted",
		"deprecated"
	]),
	fixtures: z.array(AppFixtureResultSchema),
	promotedTo: PromotedChannelSchema.nullable(),
	idempotent: z.boolean(),
	createdBy: z.string().min(1),
	/** Deprecation warnings the publish gate recorded (R-022). Absent from a CP that predates S3; `[]`
	*  from one that ran the gate and found none. Additive on the wire. */
	warnings: z.array(PublishWarningSchema).optional(),
	requestId: z.string().min(1),
	createdAt: z.string().min(1)
}).strict();
const AppWorkerDeclarationSchema = z.object({
	name: z.string().min(1),
	subscribesTo: subscribesToSchema,
	runtime: z.enum(["node", "python"]),
	entrypoint: z.string().min(1),
	sizeClass: z.string().min(1),
	maxReplicas: z.number().int().positive(),
	cooldownSeconds: z.number().int().nonnegative(),
	database: z.boolean()
}).strict();
const AppFixtureDeclarationSchema = z.object({ fixtureId: z.string().min(1) }).strict();
/**
* The wire twin of `AppShellChooserEntry`: `chooser` on an app `shell[]` entry.
*
* @deprecated 2026-09-25-layered-shell-surfaces R-132: `chooser` on a `shell[]` entry is deprecated. It
* has no counterpart on a surface; a routed surface in `surfaces[]` carries its own
* `requiresPermission`. The schema stays, with every field: the CP still persists the block of a
* `shell[]` app and the DP still reads it (R-128).
*/
const ShellEntryChooserDeclarationSchema = z.object({
	label: z.string().min(1),
	icon: z.string().min(1).optional(),
	surfaceId: z.string().regex(/^[a-z][a-z0-9-]*$/),
	order: z.number().int().optional(),
	requiresPermission: z.string().min(1).optional()
}).strict();
/**
* The wire twin of `AppShellEntry`: one app `shell[]` entry.
*
* @deprecated 2026-09-25-layered-shell-surfaces R-132: a `shell[]` entry is deprecated. Declare a routed
* surface in `surfaces[]` instead (`SurfaceDeclarationsSchema`). The schema stays, with every
* field: the CP still persists the block of a `shell[]` app, and core-api gives a `shell[]`-only
* app one routed surface `legacy.v1` from `shell[0]` (R-128, R-129).
*/
const ShellEntryDeclarationSchema = z.object({
	mountPath: mountPathSchema,
	defaultRoute: z.string().min(1),
	category: z.string().min(1),
	uiPackage: z.string().min(1),
	chooser: ShellEntryChooserDeclarationSchema.optional()
}).strict();
//#endregion
export { AppFixtureDeclarationSchema, AppWorkerDeclarationSchema, PublishWarningSchema, RegisterAppVersionDtoSchema, RegisterAppVersionResponseDtoSchema, RegisterFixtureVersionDtoSchema, ShellEntryChooserDeclarationSchema, ShellEntryDeclarationSchema, SignedReleaseStatementBodySchema };

//# sourceMappingURL=register-version.schemas.js.map